import { ACTIVE_RESPONSE_KEYS } from '@/lib/constants/activeResponses'
import { EDR_CONNECTION } from '@/lib/constants/edr'

export const EDR_DASHBOARD = {
  TITLE: 'EDR',
  SUBTITLE: 'Endpoint detection and response',
  TABS: {
    OVERVIEW: 'Overview',
    ENDPOINTS: 'Endpoints',
  },
  ENDPOINTS: {
    TITLE: 'Endpoints',
    SUBTITLE: '{count} endpoints across your fleet',
    SEARCH_PLACEHOLDER: 'Search endpoints...',
    KIND_FILTER: {
      ALL: 'All',
      LAPTOPS: 'Laptops',
      SERVERS: 'Servers',
    },
    NO_OWNER: 'N/A',
    DRAWER: {
      ALERTS: 'Alerts',
      LAST_30_DAYS: 'Last 30d',
      EMPTY: 'No events for this endpoint',
      EMPTY_ALERTS: 'No alerts for this endpoint',
      ERROR: 'Could not load alerts',
      FILTER: {
        ALERTS: 'Alerts',
        ALL: 'All events',
      },
      ACTIONS: 'Actions',
      NETWORK: 'Network',
      CANCEL: 'Cancel',
      RESTART: 'Restart agent',
      RESTART_CONFIRM_TITLE: 'Restart the agent on {hostname}?',
      RESTART_CONFIRM_BODY: 'This restarts the agent on the endpoint.',
      RESTART_CONFIRM: 'Restart',
      RESTART_SUCCESS: 'Restart requested for {hostname}',
      RESTART_ERROR: 'Could not restart the agent',
      ISOLATE: 'Isolate',
      ISOLATE_CONFIRM_TITLE: 'Isolate {hostname} from the network?',
      ISOLATE_CONFIRM_BODY:
        'This blocks all network traffic on the endpoint except its connection to the monitoring server.',
      ISOLATE_CONFIRM: 'Isolate',
      ISOLATE_SUCCESS: 'Isolation requested for {hostname}',
      ISOLATE_ERROR: 'Could not isolate the endpoint',
      RESTORE: 'Restore',
      RESTORE_CONFIRM_TITLE: 'Restore network access on {hostname}?',
      RESTORE_CONFIRM_BODY: 'This lifts the isolation and restores normal network connectivity on the endpoint.',
      RESTORE_CONFIRM: 'Restore',
      RESTORE_SUCCESS: 'Network access restore requested for {hostname}',
      RESTORE_ERROR: 'Could not restore network access',
    },
  },
  KPIS: {
    ALERTS: 'Alerts (30d)',
    ENDPOINTS_AT_RISK: 'Endpoints at risk',
    AT_RISK_DEFINITION: 'Endpoints with alerts (30d)',
    MORE: '+{count} more',
    ALL_CLEAR: 'All clear',
    OF_FLEET: '{percent}% of fleet',
    COVERAGE: 'Coverage',
    COVERAGE_DEFINITION: 'Employees without an agent installed',
    RESPONSES: 'Auto responses (30d)',
  },
  SUMMARY: {
    AGENTS_DEPLOYED: 'agents deployed',
    COVERAGE: 'coverage',
    COVERED: 'Covered',
    NOT_COVERED: 'Not covered',
    VIEW_EMPLOYEES: 'View employees',
    TOOLTIP: {
      [EDR_CONNECTION.ONLINE]: 'Endpoint is online',
      [EDR_CONNECTION.OFFLINE]: 'Endpoint is offline',
      [EDR_CONNECTION.NEVER]: 'Endpoint never connected',
    } as Record<string, string>,
  },
  TOP_AFFECTED_ENDPOINTS: {
    TITLE: 'Most alerts',
    LAST_DAYS_PREFIX: 'Last',
    LAST_DAYS_SUFFIX: 'days',
    EMPTY: 'No alerts in the last 30 days',
  },
  ALERTS_TREND: {
    TITLE: 'Alerts over time',
    LAST_DAYS_PREFIX: 'Last',
    LAST_DAYS_SUFFIX: 'days',
    CRITICAL: 'Critical',
    HIGH: 'High',
    EMPTY: 'No alerts in the last 30 days',
  },
  MITRE: {
    TITLE: 'Threat breakdown',
    SUBTITLE_PREFIX: 'MITRE ATT&CK ·',
    SUBTITLE_SUFFIX: 'alerts',
    EMPTY: 'No threat breakdown data.',
  },
  RESPONSES: {
    TITLE: 'Active response',
    SUBTITLE: 'Automated actions triggered by alerts',
    EMPTY: 'No active responses in the last 30 days',
    SHOW_ALL: 'Show all ({count})',
    SHOW_LESS: 'Show less',
    TREND: {
      OTHER: 'Other',
      NONE: 'No responses',
    },
    DRAWER: {
      RAW_LOG: 'Raw response',
      TIMELINE: 'Timeline',
    },
    // Plain-language labels keyed by ACTIVE_RESPONSE_KEYS (the source of truth for AR mechanisms).
    // Unknown mechanisms fall back to the raw rule description.
    ACTIONS: {
      [ACTIVE_RESPONSE_KEYS.ROUTE_NULL_BLOCK]: 'Isolated host from network',
      [ACTIVE_RESPONSE_KEYS.ROUTE_NULL_UNBLOCK]: 'Restored host to network',
      [ACTIVE_RESPONSE_KEYS.WIN_ROUTE_NULL_BLOCK]: 'Isolated host from network',
      [ACTIVE_RESPONSE_KEYS.WIN_ROUTE_NULL_UNBLOCK]: 'Restored host to network',
      [ACTIVE_RESPONSE_KEYS.FIREWALL_DROP_BLOCK]: 'Blocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.FIREWALL_DROP_UNBLOCK]: 'Unblocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.PF_BLOCK]: 'Blocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.PF_UNBLOCK]: 'Unblocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.NPF_BLOCK]: 'Blocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.NPF_UNBLOCK]: 'Unblocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.IPFW_BLOCK]: 'Blocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.IPFW_UNBLOCK]: 'Unblocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.NETSH_BLOCK]: 'Blocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.NETSH_UNBLOCK]: 'Unblocked source in firewall',
      [ACTIVE_RESPONSE_KEYS.HOST_DENY_BLOCK]: 'Blocked host',
      [ACTIVE_RESPONSE_KEYS.HOST_DENY_UNBLOCK]: 'Unblocked host',
      [ACTIVE_RESPONSE_KEYS.DISABLE_ACCOUNT]: 'Disabled account',
      [ACTIVE_RESPONSE_KEYS.RESTART_WAZUH]: 'Restarted agent',
      [ACTIVE_RESPONSE_KEYS.EJECT_USB_MACOS]: 'Blocked USB drive',
    } as Record<string, string>,
  },
  ACTIVITY: {
    TITLE: 'Endpoint activity',
    SUBTITLE: 'Most recently active endpoints',
    EMPTY: 'No recent endpoint activity',
    ALERTS_COUNT: '{count} alerts',
    AGENT: 'Agent',
    ALERTS: 'Alerts (30d)',
    LAST_SEEN: 'Last seen',
    VERSION: {
      UNKNOWN: 'Unknown',
      UPDATED: 'Updated',
      OUTDATED: 'Outdated',
      NO_VERSION: 'Agent version not reported',
      ON_LATEST: 'Agent is on the latest version ({version})',
      RUNNING_OLD: 'Agent is outdated: running {version}',
    },
    LAST_SEEN_FORMAT: {
      JUST_NOW: 'just now',
      MINUTES: '{value}m ago',
      HOURS: '{value}h ago',
      DAYS: '{value}d ago',
      NEVER: '—',
    },
  },
}
